import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, formatDate, formatDuration } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DollarSign, Trash2, ChevronDown, ChevronRight, Percent, Archive, Lock, AlertTriangle, Clock, Timer, Pencil, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import EditFatturaModal from "@/components/EditFatturaModal";

const fmt = (n) => Math.round(n || 0).toLocaleString("it-IT");
const fmtH = (sec) => {
  const s = Math.floor(sec || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

export default function Compensi() {
  const { user } = useAuth();
  const isDirettore = user?.role === "Proprietario";

  const [tab, setTab] = useState(isDirettore ? "cycle" : "mie");
  const [scope, setScope] = useState("current"); // current | lifetime
  const [summary, setSummary] = useState({ users: [], grand_total: 0, grand_commission: 0, grand_activity: 0 });
  const [mine, setMine] = useState({ fatture: [], total: 0, commission: 0, count: 0 });
  const [lifetime, setLifetime] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [expandedPayout, setExpandedPayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetInput, setResetInput] = useState("");
  const [editFattura, setEditFattura] = useState(null);
  // Vista fatture di un singolo dipendente (Proprietario)
  const [userFatture, setUserFatture] = useState(null); // { user, fatture, total, commission }
  const [userFattureLoading, setUserFattureLoading] = useState(false);
  const [userFattureExpanded, setUserFattureExpanded] = useState(null);

  const openUserFatture = useCallback(async (u) => {
    setUserFatture({ user: u, fatture: [], total: 0, commission: 0 });
    setUserFattureLoading(true);
    try {
      const { data } = await api.get(`/fatture/by-user/${u.user_id}?scope=${scope}`);
      setUserFatture({ user: u, ...data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setUserFatture(null);
    } finally { setUserFattureLoading(false); }
  }, [scope]);

  const reloadUserFatture = useCallback(async () => {
    if (!userFatture?.user) return;
    try {
      const { data } = await api.get(`/fatture/by-user/${userFatture.user.user_id}?scope=${scope}`);
      setUserFatture({ user: userFatture.user, ...data });
    } catch (e) { console.warn("reload user fatture:", e); }
  }, [userFatture, scope]);

  const deleteUserFattura = async (fid) => {
    if (!window.confirm("Eliminare la fattura? L'operazione è irreversibile.")) return;
    try {
      await api.delete(`/fatture/${fid}`);
      toast.success("Fattura eliminata");
      reloadUserFatture();
      loadSummary(scope);
      loadLifetime();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadSummary = useCallback((s = scope) => {
    setLoading(true);
    api.get(`/fatture/summary?scope=${s}`)
      .then((r) => setSummary(r.data))
      .finally(() => setLoading(false));
  }, [scope]);

  const loadMine = useCallback((s = scope) => {
    setLoading(true);
    api.get(`/fatture/mine?scope=${s}`)
      .then((r) => setMine(r.data))
      .finally(() => setLoading(false));
  }, [scope]);

  const loadLifetime = useCallback(() => {
    api.get("/stats/lifetime").then((r) => setLifetime(r.data)).catch(() => {});
  }, []);

  const loadPayouts = useCallback(() => {
    api.get("/payouts").then((r) => setPayouts(r.data)).catch(() => setPayouts([]));
  }, []);

  useEffect(() => {
    loadLifetime();
    if (isDirettore) loadPayouts();
  }, [loadLifetime, loadPayouts, isDirettore]);

  useEffect(() => {
    if (tab === "cycle" || tab === "lifetime-officina") loadSummary(tab === "cycle" ? "current" : "lifetime");
    else if (tab === "mie") loadMine(scope);
    else if (tab === "storico") loadPayouts();
  }, [tab, scope, loadSummary, loadMine, loadPayouts]);

  const closeCycle = async () => {
    if (!window.confirm("Confermare la chiusura del ciclo? Tutte le fatture e le ore lavorate correnti verranno archiviate e i totali dei dipendenti resettati (il totale officina rimane). Continuare?")) return;
    setClosing(true);
    try {
      const { data } = await api.post("/payouts/close-cycle");
      // Reset ottimistico immediato del ciclo corrente
      setSummary({ users: [], grand_total: 0, grand_commission: 0, grand_activity: 0, grand_seconds: 0 });
      toast.success(`Ciclo chiuso · $${fmt(data.grand_total)} archiviati · $${fmt(data.grand_commission)} da pagare · ${fmtH(data.grand_seconds)} ore`);
      // Ricarica lo storico in background
      await Promise.all([
        loadSummary("current"),
        api.get("/stats/lifetime").then((r) => setLifetime(r.data)).catch(() => {}),
        api.get("/payouts").then((r) => setPayouts(r.data)).catch(() => {}),
      ]);
      // Passa direttamente allo storico per mostrare il ciclo appena chiuso
      setTab("storico");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setClosing(false); }
  };

  const resetAll = async () => {
    if (resetInput !== "RESET") {
      toast.error("Digita 'RESET' per confermare");
      return;
    }
    try {
      await api.post("/payouts/reset-all", { confirm: "RESET" });
      toast.success("Reset completato · tutti i dati sono stati eliminati");
      setResetOpen(false);
      setResetInput("");
      loadSummary("current");
      loadLifetime();
      loadPayouts();
      if (tab === "mie") loadMine("current");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteFattura = async (id) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    try {
      await api.delete(`/fatture/${id}`);
      toast.success("Fattura eliminata");
      if (tab === "mie") loadMine();
      else loadSummary();
      loadLifetime();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const directorTabs = [
    { k: "cycle", l: "Ciclo Corrente" },
    { k: "storico", l: "Cicli Chiusi" },
    { k: "lifetime-officina", l: "Officina Sempre" },
    { k: "mie", l: "Le mie" },
  ];

  return (
    <div className="w-full">
      {closing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="closing-overlay">
          <div className="ot-panel-elev p-8 text-center">
            <div className="inline-block w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-4" />
            <div className="ot-heading text-xl font-bold text-slate-100 uppercase tracking-widest">Chiusura Ciclo</div>
            <div className="ot-mono text-xs text-slate-500 tracking-widest mt-2">Archivio fatture e ore in corso...</div>
          </div>
        </div>
      )}
      <PageHeader
        icon={DollarSign}
        title="Stipendi"
        subtitle={isDirettore ? "Fatture, cicli di pagamento e storico officina." : "Il tuo stipendio netto e le fatture registrate."}
        right={
          isDirettore ? (
            <div className="flex flex-wrap items-center gap-1 ot-panel p-1">
              {directorTabs.map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
                  className={`px-3 py-1.5 text-xs ot-heading uppercase tracking-widest rounded transition-colors ${tab === t.k ? "bg-blue-400 text-black" : "text-slate-400 hover:text-blue-400"}`}>
                  {t.l}
                </button>
              ))}
            </div>
          ) : null
        }
      />

      {/* Officina lifetime banner (always visible) */}
      {lifetime && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 ot-fade-up">
          <StatCard label="OFFICINA · FATTURATO SEMPRE" value={`$ ${fmt(lifetime.officina_total)}`} color="text-blue-400" />
          <StatCard label="OFFICINA · ORE SEMPRE" value={fmtH(lifetime.officina_seconds)} color="text-blue-400" mono />
          <StatCard label="STIPENDI SEMPRE" value={`$ ${fmt(lifetime.officina_commission)}`} color="text-amber-400" />
          <StatCard label="CICLI CHIUSI" value={lifetime.payouts_count} mono />
          {!isDirettore ? (
            <StatCard label="ORE TOTALI LAVORATE" value={fmtH(lifetime.my_seconds || 0)} color="text-blue-400" mono />
          ) : (
            <StatCard label="FATTURE TOTALI" value={lifetime.fatture_count} mono />
          )}
        </div>
      )}

      {/* Ciclo corrente / lifetime officina view */}
      {isDirettore && (tab === "cycle" || tab === "lifetime-officina") && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 ot-fade-up">
            <StatCard
              label={tab === "cycle" ? "FATTURATO CICLO CORRENTE" : "FATTURATO OFFICINA (STORICO COMPLETO)"}
              value={`$ ${fmt(summary.grand_total)}`}
              color="text-blue-400" big
            />
            <StatCard
              label={tab === "cycle" ? "STIPENDI DA PAGARE" : "STIPENDI EROGATI + CORRENTI"}
              value={`$ ${fmt(summary.grand_commission)}`}
              color="text-amber-400" big
            />
          </div>

          {tab === "cycle" && (
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button
                data-testid="close-cycle-btn"
                onClick={closeCycle}
                disabled={closing || (summary.grand_total <= 0 && (summary.grand_seconds || 0) <= 0)}
                className="ot-btn-primary flex items-center gap-2"
              >
                {closing ? (
                  <>
                    <span className="inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    Chiusura in corso...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Chiudi Ciclo & Paga Stipendi
                  </>
                )}
              </button>
              <span className="text-xs text-slate-500">Archivia fatture + ore correnti e resetta i totali dei dipendenti (il totale officina rimane).</span>
            </div>
          )}

          <div className="ot-panel-elev p-6 ot-fade-up">
            <h2 className="ot-heading text-2xl font-bold text-slate-100 mb-4">
              {tab === "cycle" ? "Ripartizione Ciclo Corrente" : "Ripartizione Storica"}
            </h2>
            {loading ? (
              <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
            ) : summary.users.filter((u) => u.fatture_count > 0).length === 0 ? (
              <div className="py-12 text-center text-slate-500">Nessuna fattura {tab === "cycle" ? "nel ciclo corrente" : ""}.</div>
            ) : (
              <SummaryTable users={summary.users} onUserClick={isDirettore ? openUserFatture : null} />
            )}
          </div>

          {tab === "lifetime-officina" && (
            <DangerZone open={resetOpen} setOpen={setResetOpen} value={resetInput} setValue={setResetInput} onReset={resetAll} />
          )}
        </>
      )}

      {/* Cicli chiusi (storico) */}
      {isDirettore && tab === "storico" && (
        <div className="ot-panel-elev p-6 ot-fade-up">
          <div className="flex items-center gap-3 mb-4">
            <Archive className="w-5 h-5 text-blue-400" />
            <h2 className="ot-heading text-2xl font-bold text-slate-100">Cicli di Pagamento Chiusi</h2>
            <div className="flex-1 h-px bg-white/5" />
            <span className="ot-mono text-[0.7rem] text-slate-500 tracking-widest">{payouts.length} CICLI</span>
          </div>
          {payouts.length === 0 ? (
            <div className="py-12 text-center text-slate-500">Nessun ciclo chiuso finora.</div>
          ) : (
            <div className="space-y-2">
              {payouts.map((p) => (
                <PayoutCard key={p.id} p={p} open={expandedPayout === p.id} onToggle={() => setExpandedPayout(expandedPayout === p.id ? null : p.id)} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Le mie */}
      {tab === "mie" && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <div className="flex flex-wrap gap-1 ot-panel p-1">
              {[
                { k: "current", l: "Corrente" },
                { k: "lifetime", l: "Storico" },
              ].map((s) => (
                <button key={s.k} onClick={() => setScope(s.k)} data-testid={`scope-${s.k}`}
                  className={`px-3 py-1.5 text-xs ot-heading uppercase tracking-widest rounded transition-colors ${scope === s.k ? "bg-blue-400 text-black" : "text-slate-400 hover:text-blue-400"}`}>
                  {s.l}
                </button>
              ))}
            </div>
            <span className="text-xs text-slate-500">
              {scope === "current" ? "Fatture del ciclo in corso (non ancora pagate)" : "Tutte le tue fatture di sempre"}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 ot-fade-up">
            <StatCard label="FATTURE" value={mine.count} mono />
            <StatCard label={scope === "current" ? "FATTURATO CORRENTE" : "FATTURATO TOTALE"} value={`$ ${fmt(mine.total)}`} color="text-blue-400" />
            <StatCard label={scope === "current" ? "STIPENDIO NETTO DA RICEVERE" : "STIPENDIO NETTO STORICO"} value={`$ ${fmt(mine.commission)}`} color="text-amber-400" />
          </div>

          <div className="ot-panel-elev p-6 ot-fade-up" style={{ animationDelay: "80ms" }}>
            <h2 className="ot-heading text-2xl font-bold text-slate-100 mb-4">
              {scope === "current" ? "Fatture Correnti" : "Storico Fatture"}
            </h2>
            {mine.fatture.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                {scope === "current"
                  ? "Nessuna fattura nel ciclo corrente. Vai su 'Aiuto Fatture' per crearne una."
                  : "Nessuna fattura registrata."}
              </div>
            ) : (
              <div className="space-y-2">
                {mine.fatture.map((f) => (
                  <FatturaRow key={f.id} f={f} expanded={expanded === f.id} onToggle={() => setExpanded(expanded === f.id ? null : f.id)} onDelete={() => deleteFattura(f.id)} onEdit={() => setEditFattura(f)} canDelete={isDirettore} canEdit={isDirettore} />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {userFatture && (
        <UserFattureModal
          data={userFatture}
          loading={userFattureLoading}
          scope={scope}
          expanded={userFattureExpanded}
          onToggleExpand={(id) => setUserFattureExpanded(userFattureExpanded === id ? null : id)}
          onEdit={(f) => setEditFattura(f)}
          onDelete={(fid) => deleteUserFattura(fid)}
          onClose={() => { setUserFatture(null); setUserFattureExpanded(null); }}
        />
      )}

      {editFattura && (
        <EditFatturaModal
          fattura={editFattura}
          onClose={() => setEditFattura(null)}
          onSaved={() => {
            setEditFattura(null);
            loadMine(scope);
            loadLifetime();
            loadSummary(scope);
            reloadUserFatture();
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color = "text-slate-100", big, mono }) {
  return (
    <div className="ot-panel p-5">
      <div className="ot-label mb-2">{label}</div>
      <div className={`${mono ? "ot-mono" : "ot-heading"} font-black ${big ? "text-4xl" : "text-3xl"} ${color}`}>{value}</div>
    </div>
  );
}

function SummaryTable({ users, onUserClick }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b border-white/5">
            <th className="ot-label py-3 font-normal">Dipendente</th>
            <th className="ot-label py-3 font-normal">Ruolo</th>
            <th className="ot-label py-3 font-normal text-right"><Percent className="w-3 h-3 inline" /></th>
            <th className="ot-label py-3 font-normal text-right">Ore</th>
            <th className="ot-label py-3 font-normal text-right">Fatture</th>
            <th className="ot-label py-3 font-normal text-right">Fatturato</th>
            <th className="ot-label py-3 font-normal text-right">Stipendio Netto</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const clickable = !!onUserClick && u.fatture_count > 0;
            return (
            <tr
              key={u.user_id}
              onClick={clickable ? () => onUserClick(u) : undefined}
              className={`border-b border-white/5 transition-colors ${clickable ? "cursor-pointer hover:bg-blue-400/[0.06]" : "hover:bg-blue-400/[0.02]"} ${(u.fatture_count === 0 && (u.shifts_count || 0) === 0) ? "opacity-40" : ""}`}
              data-testid={`summary-row-${u.username}`}
              title={clickable ? "Clicca per vedere le fatture" : ""}
            >
              <td className="py-3">
                <div className={`ot-heading font-bold uppercase text-sm ${clickable ? "text-blue-300 hover:text-blue-200" : "text-slate-100"}`}>{u.display_name}</div>
                <div className="ot-mono text-[0.65rem] text-slate-500">@{u.username}</div>
              </td>
              <td className="py-3 ot-mono text-[0.7rem] text-blue-400/80 tracking-widest">{u.role}</td>
              <td className="py-3 text-right ot-mono text-slate-400">{Math.round(u.percent * 100)}%</td>
              <td className="py-3 text-right ot-mono text-slate-300">{fmtH(u.total_seconds || 0)}</td>
              <td className="py-3 text-right ot-mono text-slate-300">{u.fatture_count}</td>
              <td className="py-3 text-right ot-mono text-blue-400 font-bold">$ {fmt(u.total)}</td>
              <td className="py-3 text-right ot-mono text-amber-400 font-black">$ {fmt(u.commission)}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FatturaRow({ f, expanded, onToggle, onDelete, onEdit, canDelete, canEdit }) {
  return (
    <div className="ot-panel">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 hover:bg-blue-400/[0.02] transition-colors" data-testid={`fattura-${f.id}`}>
        {expanded ? <ChevronDown className="w-4 h-4 text-blue-400" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm text-slate-200 truncate flex items-center gap-2">
            {f.vehicle_name ? <span className="text-blue-400">{f.vehicle_name}</span> : "Fattura"}
            {f.paid_at && (
              <span className="ot-mono text-[0.6rem] text-emerald-400 tracking-widest border border-emerald-400/40 rounded px-1.5 py-0.5">PAGATO</span>
            )}
          </div>
          <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest">{formatDate(f.created_at)}{f.updated_at ? ` · modificata ${formatDate(f.updated_at)}` : ""}</div>
        </div>
        <div className="text-right">
          <div className="ot-mono font-bold text-blue-400">$ {fmt(f.total)}</div>
          <div className="ot-mono text-[0.65rem] text-amber-400 tracking-widest">+ $ {fmt(f.commission)}</div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/5 p-4 bg-black/20">
          <div className="space-y-1 mb-3">
            {(f.items || []).map((it, idx) => (
              <div key={`${it.key || 'itm'}-${idx}`} className="flex items-center justify-between text-xs">
                <span className="text-slate-300">{it.label} × {it.qty}</span>
                <span className="ot-mono text-slate-400">$ {fmt(it.subtotal)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between text-xs pt-3 border-t border-white/5">
            <div className="text-slate-500">Ruolo <span className="text-blue-400">{f.role}</span> · {Math.round((f.percent || 0) * 100)}%{f.paid_at && ` · Pagato ${formatDate(f.paid_at)}`}</div>
            <div className="flex items-center gap-3">
              {canEdit && (
                <button onClick={onEdit} data-testid={`edit-fattura-${f.id}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                  <Pencil className="w-3 h-3" /> Modifica
                </button>
              )}
              {canDelete && (
                <button onClick={onDelete} data-testid={`delete-fattura-${f.id}`} className="text-red-400 hover:text-red-300 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> Elimina
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutCard({ p, open, onToggle }) {
  return (
    <div className="ot-panel">
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-blue-400/[0.02] transition-colors" data-testid={`payout-${p.id}`}>
        {open ? <ChevronDown className="w-4 h-4 text-blue-400" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <Clock className="w-4 h-4 text-blue-400/70" />
        <div className="flex-1 text-left">
          <div className="ot-heading font-bold text-slate-100 uppercase text-sm">Ciclo del {formatDate(p.closed_at)}</div>
          <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest">
            {p.fatture_count || 0} fatture · {p.shifts_count || 0} turni · Chiuso da @{p.closed_by}
          </div>
        </div>
        <div className="text-right">
          <div className="ot-mono font-bold text-blue-400">$ {fmt(p.grand_total)}</div>
          <div className="ot-mono text-[0.65rem] text-amber-400 tracking-widest">STIPENDI $ {fmt(p.grand_commission)}</div>
          <div className="ot-mono text-[0.6rem] text-slate-500 tracking-widest mt-0.5"><Timer className="w-2.5 h-2.5 inline mr-0.5" />{fmtH(p.grand_seconds || 0)}</div>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5 p-4 bg-black/20">
          <SummaryTable users={p.users || []} />
        </div>
      )}
    </div>
  );
}

function UserFattureModal({ data, loading, scope, expanded, onToggleExpand, onEdit, onDelete, onClose }) {
  const u = data.user;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="user-fatture-modal">
      <div className="ot-panel-elev max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-[#0a1024] border-b border-blue-400/20 p-5 flex items-center justify-between">
          <div>
            <div className="ot-label">// FATTURE DIPENDENTE · {scope === "current" ? "CICLO CORRENTE" : "STORICO"}</div>
            <h2 className="ot-heading text-2xl font-black uppercase tracking-tight text-slate-100">{u.display_name}</h2>
            <div className="ot-mono text-[0.65rem] text-blue-400/80 tracking-widest mt-0.5">
              @{u.username} · {u.role} · {Math.round((u.percent || 0) * 100)}%
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded transition-colors" data-testid="close-user-fatture">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="ot-panel p-4">
              <div className="ot-label mb-1">FATTURE</div>
              <div className="ot-mono text-2xl font-black text-slate-100">{data.count || 0}</div>
            </div>
            <div className="ot-panel p-4">
              <div className="ot-label mb-1">FATTURATO</div>
              <div className="ot-mono text-2xl font-black text-blue-400">$ {fmt(data.total)}</div>
            </div>
            <div className="ot-panel p-4">
              <div className="ot-label mb-1">STIPENDIO NETTO</div>
              <div className="ot-mono text-2xl font-black text-amber-400">$ {fmt(data.commission)}</div>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-500 ot-mono">Caricamento...</div>
          ) : (data.fatture || []).length === 0 ? (
            <div className="py-12 text-center text-slate-500">Nessuna fattura registrata.</div>
          ) : (
            <div className="space-y-2">
              {(data.fatture || []).map((f) => (
                <FatturaRow
                  key={f.id}
                  f={f}
                  expanded={expanded === f.id}
                  onToggle={() => onToggleExpand(f.id)}
                  onEdit={() => onEdit(f)}
                  onDelete={() => onDelete(f.id)}
                  canEdit={true}
                  canDelete={true}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DangerZone({ open, setOpen, value, setValue, onReset }) {
  return (
    <div className="mt-6 border border-red-500/30 rounded p-5 bg-red-500/[0.03]">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <div className="ot-heading text-lg font-bold text-red-400 uppercase tracking-tight">Zona Pericolosa</div>
      </div>
      <p className="text-slate-400 text-sm mb-3">
        Reset totale officina: elimina TUTTE le fatture, TUTTI i turni registrati e TUTTI i cicli chiusi. Il fatturato e le ore di sempre vengono azzerati (i turni attivi in corso restano). Operazione irreversibile.
      </p>
      {!open ? (
        <button data-testid="open-reset-btn" onClick={() => setOpen(true)} className="ot-btn-danger">
          Reset Totale Officina
        </button>
      ) : (
        <div className="space-y-3">
          <label className="ot-label block">Digita <span className="text-red-400">RESET</span> per confermare</label>
          <input
            data-testid="reset-confirm-input"
            className="ot-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="RESET"
          />
          <div className="flex gap-2">
            <button data-testid="reset-confirm-btn" onClick={onReset} className="ot-btn-danger flex-1">Conferma Reset</button>
            <button onClick={() => { setOpen(false); setValue(""); }} className="ot-btn-ghost">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
